import { PermissionDeniedPageComponent } from '@shared/pages/permission-denied-page/permission-denied-page.component';
import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { WrapperComponent } from './wrapper/wrapper.component';
import { AuthGuard } from '@core/guard/auth-guard.guard';
import { RoutesUtil } from '@shared-features/utils/routes.util';
import { NotFoundPage } from '@shared/pages/not-found/not-found.page';
import { NoAuthGuard } from '@core/guard/no-auth-guard.guard';

const routes: Routes = [
  {
    path: RoutesUtil.Auth.path,
    loadChildren: () => import(/* webpackChunkName: "Auth" */ './features/auth/auth.module').then((m) => m.AuthModule),
  },
  {
    path: RoutesUtil.Public.path,
    loadChildren: () => import(/* webpackChunkName: "Public" */ './features/public/public.module').then((m) => m.PublicModule),
  },
  {
    path: RoutesUtil.Booking.path,
    canActivate: [NoAuthGuard],
    children: [
      {
        path: '',
        loadChildren: () => import(/* webpackChunkName: "OnlineBooking" */ './features/online-booking/online-booking.module').then((m) => m.OnlineBookingModule),
      },
    ],
  },
  {
    path: RoutesUtil.Guest.path,
    loadChildren: () =>
      import(/* webpackChunkName: "Auth" */ './features/distribute-fleets/distribute-fleets.module').then((m) => m.DistributeFleetsModule),
  },
  {
    path: RoutesUtil.NotFound.path,
    component: NotFoundPage,
  },
  {
    path: '',
    component: WrapperComponent,
    canActivate: [AuthGuard],
    runGuardsAndResolvers: 'always',
    children: [
      {
        path: RoutesUtil.Dashboards.path,
        loadChildren: () =>
          import(/* webpackChunkName: "Dashboards" */ './features/dashboard/dashboard.module').then((m) => m.DashboardModule),
      },
      {
        path: RoutesUtil.Shipments.path,
        loadChildren: () =>
          import(/* webpackChunkName: "Shipments" */ './features/shipments/shipments.module').then((m) => m.ShipmentsModule),
      },
      {
        path: RoutesUtil.ShipmentTemplate.path,
        loadChildren: () =>
          import(
            /* webpackChunkName: "ShipmentCreationTemplate" */ '@features/shipment-creation-template/shipment-creation-template.module'
          ).then(({ ShipmentCreationTemplateModule }) => ShipmentCreationTemplateModule),
      },

      {
        path: RoutesUtil.Settings.path,
        loadChildren: () => import(/* webpackChunkName: "Settings" */ './features/settings/settings.module').then((m) => m.SettingsModule),
      },
      {
        path: RoutesUtil.Customers.path,
        loadChildren: () =>
          import(/* webpackChunkName: "Customers" */ './features/customers/customers.module').then((m) => m.CustomersModule),
      },
      {
        path: RoutesUtil.Fleets.path,
        loadChildren: () => import(/* webpackChunkName: "Fleets" */ './features/fleets/fleets.module').then((m) => m.FleetsModule),
      },
      {
        path: RoutesUtil.Support.path,
        loadChildren: () => import(/* webpackChunkName: "Support" */ './features/support/support.module').then((m) => m.SupportModule),
      },
      {
        path: RoutesUtil.TripPlanning.path,
        loadChildren: () =>
          import(/* webpackChunkName: "TripPlanning" */ './features/trip-planning/trip-planning.module').then((m) => m.TripPlanningModule),
      },
      /**
       * @todo: (@mhassan)
       * UsersModule & Contracts Module
       * are imported by other modules
       * find a suitable solution to share what is
       * common between them then enable lazy load for it.
       */
      // {
      //   path: RoutesConstants.REPORTS_PATH,
      //   component: RouterContainerComponent,
      //   loadChildren: () => import(/* webpackChunkName: "Reports" */'./ui/contracts/contracts.module')
      //     .then(m => m.ContractsModule),
      // },

      {
        path: RoutesUtil.Receivers.path,
        loadChildren: () =>
          import(/* webpackChunkName: "Receivers" */ './features/receivers/receivers.module').then((m) => m.ReceiversModule),
      },
      {
        path: RoutesUtil.LiveTracking.path,
        loadChildren: () =>
          import(/* webpackChunkName: "LiveTracking" */ './features/live-tracking/live-tracking.module').then((m) => m.LiveTrackingModule),
      },
      {
        path: RoutesUtil.Invoices.path,
        loadChildren: () => import(/* webpackChunkName: "Invoices" */ './features/invoices/invoices.module').then((m) => m.InvoicesModule),
      },

      {
        path: RoutesUtil.Transactions.path,
        loadChildren: () => import('./features/transactions/transactions.module').then((m) => m.TransactionsModule),
      },

      {
        path: RoutesUtil.Reports.path,
        loadChildren: () => import(/* webpackChunkName: "Reports" */ './features/reports/reports.module').then((m) => m.ReportsModule),
      },

      {
        path: RoutesUtil.Contracts.path,
        loadChildren: () =>
          import(/* webpackChunkName: "Contracts" */ './features/contracts/contracts.module').then((m) => m.ContractsModule),
      },
      {
        path: RoutesUtil.Users.path,
        loadChildren: () => import(/* webpackChunkName: "Users" */ './features/users/users.module').then((m) => m.UsersModule),
      },
      {
        path: RoutesUtil.TransportationPayments.path,
        loadChildren: () =>
          import(/* webpackChunkName: "TransportationPayments" */ './features/transportation-payments/transportation-payments.module').then(
            (m) => m.TransportationPaymentsModule
          ),
      },
      {
        path: RoutesUtil.Payment.path,
        loadChildren: () =>
          import(/* webpackChunkName: "TransportationPayments" */ './features/payment/payment.module').then((m) => m.PaymentModule),
      },
      {
        path: RoutesUtil.Orders.path,
        loadChildren: () => import(/* webpackChunkName: "orders" */ './features/orders/orders.module').then((m) => m.OrdersModule),
      },
      {
        path: RoutesUtil.Logs.path,
        loadChildren: () => import(/* webpackChunkName: "logs" */ './features/logs/logs-module').then((m) => m.LogsModule),
      },
      {
        path: RoutesUtil.AccessDenied.path,
        component: PermissionDeniedPageComponent,
      },
      {
        path: RoutesUtil.NotFound.path,
        component: NotFoundPage,
      },
      { path: '', redirectTo: RoutesUtil.Home.url(), pathMatch: 'full' },
      { path: '**', redirectTo: RoutesUtil.Home.url() },
    ],
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      enableTracing: false,
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
